import { css } from 'lit';
import { DraftEvent, LaneBlockage, LaneImpactValue, RouteClassification } from '../../typings/api';
import { isLaneImpacted } from './utils';
import REMState from '../redux/rem/rem-state';
import { ConfigREMEventForm } from '../config/ConfigREM';
import { EventTypeConfig } from '../config/ConfigREMEventTypes';

export const isRampAffected = (laneImpact?: LaneBlockage | null) =>
	laneImpact?.entranceRampAffected || laneImpact?.exitRampAffected;

export const shoulderIsAffected = (laneImpact?: LaneBlockage | null) =>
	laneImpact?.insideShoulderAffected || laneImpact?.outsideShoulderAffected;

export const roadIsClosed = (event: DraftEvent): boolean => {
	const { locationDetails, positiveLaneBlockage, negativeLaneBlockage } = event;

	return (
		(locationDetails?.lanes?.positive?.laneCount != null &&
			locationDetails?.lanes?.positive?.laneCount ===
				positiveLaneBlockage?.lanesAffected?.length) ||
		(locationDetails?.lanes?.negative?.laneCount != null &&
			locationDetails?.lanes?.negative?.laneCount === negativeLaneBlockage?.lanesAffected?.length)
	);
};

export const determinePriorityForLaneImpact = (
	laneImpact: LaneBlockage,
	laneCount: number,
	routeClassification: RouteClassification,
): number => {
	const percentAffected = laneImpact.lanesAffected
		? laneImpact.lanesAffected.length / laneCount
		: 0;

	if (percentAffected === 1) {
		if (routeClassification === RouteClassification.INTERSTATE) {
			return 1;
		}
		return 2;
	}
	if (percentAffected >= 0.01 && percentAffected <= 0.99) {
		if (
			routeClassification === RouteClassification.STATE ||
			routeClassification === RouteClassification.US
		) {
			return 3;
		}
		if (percentAffected >= 0.51 && percentAffected <= 0.99) {
			return 2;
		}
		return 3;
	}
	if (laneImpact.insideShoulderAffected || laneImpact.outsideShoulderAffected) {
		return 4;
	}

	if (isRampAffected(laneImpact)) {
		if (routeClassification === RouteClassification.INTERSTATE) {
			return 1;
		}
		if (
			routeClassification === RouteClassification.STATE ||
			routeClassification === RouteClassification.US
		) {
			return 2;
		}
	}

	return 5;
};

export const determineEventPriority = (state: REMState, event: DraftEvent): number => {
	const { eventType } = event;

	if (ConfigREMEventForm.priority.determinePriorityByUnplannedEvents) {
		return roadIsClosed(event) && eventType && EventTypeConfig[eventType]?.unplannedEvent ? 1 : 5;
	}

	// if the priority isn't configured to be automatically calculated, or neither lane is impacted, use the default priority
	if (
		ConfigREMEventForm.priority.determinePriorityByLaneImpacts === false ||
		((!isLaneImpacted(event.positiveLaneBlockage) ||
			event.positiveLaneBlockageType === LaneImpactValue.NA) &&
			(!isLaneImpacted(event.negativeLaneBlockage) ||
				event.negativeLaneBlockageType === LaneImpactValue.NA))
	) {
		return (
			state.eventFields?.eventTypes?.find((eventType) => eventType.name === event.eventType)
				?.defaultPriority?.priorityLevel ?? 5
		);
	}

	const priorityFallback =
		state.eventFields?.eventTypes?.find((eventType) => eventType.name === event.eventType)
			?.defaultPriority?.priorityLevel ?? 5;

	let positivePriority =
		event.positiveLaneBlockage && event.positiveLaneBlockageType === LaneImpactValue.CLOSED
			? determinePriorityForLaneImpact(
					event.positiveLaneBlockage,
					event.locationDetails?.lanes?.positive?.laneCount ?? NaN,
					event.locationDetails?.classification as RouteClassification,
			  )
			: priorityFallback;

	// slow shoulder impacts should always be priority 4
	if (
		event.positiveLaneBlockageType === LaneImpactValue.SLOW &&
		shoulderIsAffected(event.positiveLaneBlockage)
	) {
		positivePriority = 4;
	}

	if (
		isRampAffected(event.positiveLaneBlockage) &&
		event.positiveLaneBlockageType === LaneImpactValue.SLOW
	) {
		positivePriority = 3;
	}

	let negativePriority =
		event.negativeLaneBlockage && event.negativeLaneBlockageType === LaneImpactValue.CLOSED
			? determinePriorityForLaneImpact(
					event.negativeLaneBlockage,
					event.locationDetails?.lanes?.negative?.laneCount ?? NaN,
					event.locationDetails?.classification as RouteClassification,
			  )
			: priorityFallback;

	// slow shoulder impacts should always be priority 4
	if (
		event.negativeLaneBlockageType === LaneImpactValue.SLOW &&
		shoulderIsAffected(event.negativeLaneBlockage)
	) {
		negativePriority = 4;
	}

	if (
		isRampAffected(event.negativeLaneBlockage) &&
		event.negativeLaneBlockageType === LaneImpactValue.SLOW
	) {
		negativePriority = 3;
	}

	return positivePriority < negativePriority ? positivePriority : negativePriority;
};
